import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { RestService } from '../../../services/rest/rest.service';
import { CommonService } from '../../../services/common/common.service';
import { DREnumType, Endpoint, MessageType, ReviewSystem, UpdateStatusType } from '../../../models/enums';
import { SelectDocumentFolderModalComponent } from '../select-document-folder-modal/select-document-folder-modal/select-document-folder-modal.component';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';

@Component({
    standalone: false,
    selector: 'export-to-review-modal',
    templateUrl: './export-to-review-modal.component.html',
    styleUrls: ['./export-to-review-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ExportToReviewModalComponent extends BaseModalComponent implements OnInit {
    readonly defaultButtonLabel = 'Select ...';
    modalAutomationId = 'export-to';
    modalClass = 'export-to-review-modal';
    primaryButtonLabel = 'Export';
    secondaryButtonLabel = 'Cancel';
    modalTitle = 'Export to ';
    documentCountText = '';
    isReefReviewProject = false;
    documentFolderPath = this.defaultButtonLabel;
    folderByOptions: Array<string>;
    createReviewFolder = false;
    folderByMetadataField: string;
    volumeLabelValue: string;
    isFolderPathSelected = false;
    reviewFolderId: number;
    reviewFolderName: string;
    reviewFolderPath: string;
    isBusy = false;
    isBusyMessage : string;
    selectedReviewProjectId: number;
    availableRelativityProjects = [];

    constructor(private modalService: BsModalService, private modalRef: BsModalRef, private restService: RestService, private commonService: CommonService) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        this.documentCountText = `${this.modalData?.viewDocumentCount} document/s will be exported using the default settings.`;
        this.isReefReviewProject = this.commonService.currentProject?.reviewSystem === ReviewSystem.REEF_REVIEW;
        
        this.modalTitle += this.isReefReviewProject ? 'Reef Review' : 'Relativity';
        this.isBusy = true;
        this.isBusyMessage = 'Loading ...';
       
        this.loadFolderByOptions();
        if (this.isRelativityProject()) {
            this.availableRelativityProjects = this.commonService.currentProject?.reviewProjects?.length > 0 ? this.commonService.currentProject?.reviewProjects : [];
            if (this.availableRelativityProjects?.length === 1) {
                this.selectedReviewProjectId = this.availableRelativityProjects[0].reviewProjectId;
            }
        }
    }

    isRelativityProject(): boolean {
        return this.commonService.currentProject?.reviewSystem === ReviewSystem.RELATIVITY;
    }

    loadFolderByOptions() {
        this.restService.post(Endpoint.realmManager_getDREnumNames, { enumType: DREnumType.FOLDER_BY_FIELD}).then(response => {
            if (response?.names?.length > 0) {
                response?.names?.unshift('');
                this.folderByOptions = response.names;
                this.isBusy = false;
            }
        }, () => {
            this.isBusy = false;
        });
    }

    export(): void {
        if (this.modalData.entityView) {
            const handle = this.modalData.entityView.handle;
            const selectedTagHandle = this.modalData.entityView?.attributes?.find(a => a.name === 'tagHandle')?.value;
            if (selectedTagHandle !== this.commonService.currentProject?.tlsDefaultTag) {
                this.isBusy = true;
                this.isBusyMessage = 'Tagging view for export ...';
                this.restService.post(Endpoint.viewManager_tagViews, {
                    destinationWorkingSetHandle: this.commonService.projectData?.handle,
                    tagList: [{ tagHandle: this.commonService.currentProject.tlsDefaultTag, tagState: 'ON' }],
                    taskDescription: 'Tagging view ' + this.modalData.entityView.name,
                    viewHandleList: [handle]
                }).then((tagViewResponse) => {
                    if (tagViewResponse) {
                        this.exportTagViewToReview(handle);
                    } else {
                        this.isBusy = false;
                    }
                }, () => {
                    this.isBusy = false;
                });
            } else {
                this.isBusy = true;
                this.exportTagViewToReview(handle);
            }
        }
    }

    exportTagViewToReview(viewHandle: string): void {
        if (viewHandle && this.commonService.currentProject?.tlsDefaultExportStream) {
            if (this.folderByMetadataField) {
                this.commonService.currentProject.tlsDefaultExportStream.options.folderByMetadataField = this.folderByMetadataField;
            }
            if (this.isFolderPathSelected && this.reviewFolderId) {
                this.commonService.currentProject.tlsDefaultExportStream.options.reviewFolderId = this.reviewFolderId;
                this.commonService.currentProject.tlsDefaultExportStream.options.reviewFolderName = this.reviewFolderName;
                this.commonService.currentProject.tlsDefaultExportStream.options.reviewFolderPath = this.reviewFolderPath;
            } else if (this.volumeLabelValue) {
                this.commonService.currentProject.tlsDefaultExportStream.options.volumeLabel = this.volumeLabelValue;
                this.commonService.currentProject.tlsDefaultExportStream.options.createReviewFolder = true;
            }
            if (this.isRelativityProject()) {
                this.commonService.currentProject.tlsDefaultExportStream.options.reviewProjectId = this.selectedReviewProjectId;
                this.commonService.currentProject.tlsDefaultExportStream.options.reviewSystem = ReviewSystem.RELATIVITY;
            } else {
                this.commonService.currentProject.tlsDefaultExportStream.options.reviewProjectId = this.commonService.currentProject.reviewProjectId;
                this.commonService.currentProject.tlsDefaultExportStream.options.reviewSystem = ReviewSystem.REEF_REVIEW;
            }
            const taskDescription = `Exporting ${this.commonService.currentProject.tlsDefaultExportStream.name} at ${this.commonService.currentProject.tlsDefaultExportStream.options.dataAreaName} : ${this.commonService.currentProject.name} - ${this.commonService.currentProject.tlsDefaultExportStream.name}`;
            const request: any = {
                exportStreamHandle: this.commonService.currentProject.tlsDefaultExportStream.handle,
                srcViewHandle: viewHandle,
                taskDescription: taskDescription,
                exportStreamOptions: this.commonService.currentProject.tlsDefaultExportStream.options,
                metadataTemplateHandle: this.commonService.currentProject.tlsDefaultExportStream.options.metadataTemplateHandle,
            };
            this.isBusyMessage = 'Exporting ...';
            this.restService.post(Endpoint.groupViewManager_exportView, request).then(() => {
                this.isBusy = false;
                this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.EXPORT_VIEW);
                this.closeModal();
            }, () => {
                this.isBusy = false;
            });
            this.closeModal();
        } else {
            this.isBusy = false;
        }
    }

    onCreateFolderChanged(): void {
        if (this.createReviewFolder) {
            this.isFolderPathSelected = false;
            this.documentFolderPath = this.defaultButtonLabel;
            this.reviewFolderId = 0;
            this.reviewFolderName = '';
            this.reviewFolderPath = '';
        } else {
            this.volumeLabelValue = '';
        }
    }

    isPrimaryButtonEnabled(): boolean {
        const commonCondition = (this.isFolderPathSelected || (this.volumeLabelValue !== undefined && this.volumeLabelValue !== '')) && !this.isBusy;
        return this.isReefReviewProject ? commonCondition : (this.selectedReviewProjectId > 0 && commonCondition);
    }

    openDocumentFolderModal() {
        const reviewProjectId = this.selectedReviewProjectId || this.commonService.currentProject.reviewProjectId;
        const reviewProject = this.commonService.currentProject?.reviewProjects.find(reviewProj => reviewProj.reviewProjectId === reviewProjectId);
        const projectInfo = { 
            name: reviewProject ? reviewProject.reviewProjectName : null, 
            id: this.isRelativityProject() ? this.selectedReviewProjectId : this.commonService.currentProject.reviewProjectId, 
            reviewSystem: this.commonService.currentProject.reviewSystem 
        };
        this.commonService.openModal(SelectDocumentFolderModalComponent, { class: 'vertical-px-700 horizontal-px-538', modalData: { reviewProjectInfo: projectInfo } }).then(response => {
            if (response?.modalData) {
                let reviewFolderPath = '';
                if (response.modalData instanceof GenericTreeNode) {
                    reviewFolderPath = response.modalData.data?.name ? response.modalData.data.name.replace(/\//g, ' \\ ') : '';
                    this.reviewFolderId = response.modalData.data?.id;
                    this.reviewFolderName = response.modalData.data?.name;
                } else {
                    reviewFolderPath = response.modalData.parentFolderName + ' \\ ' + (response.modalData.subfolder?.data?.name ? response.modalData.subfolder.data.name.replace(/\//g, ' \\ ') : '');
                    this.reviewFolderId = response.modalData.subfolder?.data?.id;
                    this.reviewFolderName = response.modalData.parentFolderName + '\\' + response.modalData.subfolder?.data?.name;
                }
                
                this.isFolderPathSelected = true;
                const isAllDocs = reviewFolderPath === 'All Document Folders';
                this.documentFolderPath = reviewFolderPath === '' || isAllDocs ? 'All Document Folders' : 'All Document Folders \\ ' + reviewFolderPath;
                this.reviewFolderPath = this.documentFolderPath;
            }
        });
    }
}
